import { useForm } from '@formspree/react';
import { Button, twButton } from '@internal/ui/components/Button';
import { twIconButton } from '@internal/ui/components/IconButton';
import { StatusIndicator } from '@internal/ui/components/StatusIndicator';
import { Textarea } from '@internal/ui/components/Textarea';
import { TextInput } from '@internal/ui/components/TextInput';
import { Breakpoint } from '@internal/ui/css/breakpoints';
import AlertIcon from '@internal/ui/svg/alert.svg';
import CheckIcon from '@internal/ui/svg/check.svg';
import CloseIcon from '@internal/ui/svg/close.svg';
import UploadIcon from '@internal/ui/svg/upload.svg';
import axios from 'axios';
import classNames from 'classnames';
import Footer from 'components/Footer';
import NavLoggedIn from 'components/NavLoggedIn';
import SEO from 'components/SEO';
import StandardLayout from 'components/StandardLayout';
import { MDXRemote } from 'next-mdx-remote';
import Link from 'next/link';
import { ellipsis, rem } from 'polished';
import { FormEvent, useRef, useState } from 'react';
import { track } from 'state/app';
import { useAppDispatch } from 'state/hooks';
import styled, { css } from 'styled-components';
import * as timeago from 'timeago.js';
import { AppComponent } from 'typescript/typings';
import JobDetail from '../JobDetail';
import listings from '../jobs.json';
import { Base } from '@internal/ui/palette/base';
import {
    fontMedium,
    fontNormal,
    globalBodyPrimary,
    globalBodySecondary,
    globalCtaSmall,
    globalTitle,
    heading3,
} from '@internal/ui/typography';

interface JobListing {
    source: any;
    listing: typeof listings[0];
}

const isNew = (createdOn: Date) => {
    return Date.now() - createdOn.getTime() < 14 * 24 * 60 * 60 * 1000; // less than 2 weeks old
};

const LEVER_RETRY_DELAY = 1000;
const MAX_LEVER_RETRIES = 3;

const JobListing: AppComponent<JobListing> = ({ source, listing }) => {
    const dispatch = useAppDispatch();

    const [resumeFileName, setResumeFileName] = useState('');
    const [resumeFileError, setResumeFileError] = useState(false);
    const [state, handleSubmit] = useForm('mlezbqyl');
    const [submittingLever, setSubmittingLever] = useState(false);
    const [leverSubmitted, setLeverSubmitted] = useState(false);
    const [_leverError, setLeverError] = useState(false);

    const leverRetries = useRef(0);
    const leverRetryTimeout = useRef<ReturnType<typeof setTimeout>>(null);

    const submitLeverWithRetry = async (fields: any) =>
        new Promise<void>((resolve, reject) => {
            try {
                axios
                    .post(
                        `https://api.lever.co/v0/postings/Playback/${listing.leverId}?key=${process.env.LEVER_API_KEY}`,
                        {
                            name: fields.name,
                            email: fields.email,
                            resume: fields.resume,
                            comments: fields.summary,
                            urls: {
                                linkedin: fields.linkedin,
                                other: fields.website,
                            },
                            source: 'Playback Jobs Page',
                        }
                    )
                    .then(() => resolve());
            } catch (e) {
                console.log(e.code);
                if (e.code === '429' && leverRetries.current < MAX_LEVER_RETRIES) {
                    leverRetryTimeout.current = setTimeout(() => {
                        submitLeverWithRetry(fields);
                    }, leverRetries.current * LEVER_RETRY_DELAY);

                    leverRetries.current += 1;
                } else {
                    reject(e);
                }
            }
        });

    const submitLeverApplication = async (e: FormEvent<HTMLFormElement>) => {
        const formData = new FormData(e.currentTarget);
        const fields: any = {};
        for (const [key, value] of formData.entries()) {
            fields[key] = value;
        }

        try {
            setSubmittingLever(true);
            await submitLeverWithRetry({
                name: fields.name,
                email: fields.email,
                resume: fields.resume,
                comments: fields.summary,
                urls: {
                    linkedin: fields.linkedin,
                    other: fields.website,
                },
                source: 'Playback Jobs Page',
            });
            setLeverSubmitted(true);
            setSubmittingLever(false);
        } catch (e) {
            setSubmittingLever(false);
            setLeverError(true);
        }
    };

    const formDisabled = state.submitting || state.succeeded || submittingLever || leverSubmitted;

    return (
        <JobListingStyled>
            <SEO
                title={`${listing.title} | Jobs at Playback`}
                description={`Come build the future of watching sports. ${listing.blurb}`}
            />
            <NavLoggedInStyled />
            <ContentsStyled>
                <InnerStyled>
                    <ListingStyled>
                        <DateStyled>
                            {isNew(new Date(listing.createdOn)) && (
                                <StatusIndicator variant="positive" className="mr-4">
                                    NEW
                                </StatusIndicator>
                            )}
                            <ParagraphStyled>{timeago.format(listing.createdOn)}</ParagraphStyled>
                        </DateStyled>
                        <HeaderStyled className="border-b border-secondary-400">
                            <TitleSectionStyled>
                                <PrimaryHeadingStyled>{listing.title}</PrimaryHeadingStyled>
                                <ParagraphStyled>{listing.blurb}</ParagraphStyled>
                                <Button
                                    className="mt-7 hidden w-[170px] self-start sm:flex"
                                    variant="positive"
                                    text="Apply now"
                                    onClick={() => {
                                        document
                                            .querySelector('#apply')
                                            .scrollIntoView({ behavior: 'smooth' });
                                    }}
                                />
                            </TitleSectionStyled>
                            <DetailsStyled>
                                <JobDetail label="Salary" values={[listing.salary]} />
                                <JobDetail label="Equity" values={[listing.equity]} />
                                <JobDetail label="Location" values={listing.location} />
                                <JobDetail label="Experience" values={[listing.experience]} />
                                <JobDetail label="Skills" values={listing.skills} />
                            </DetailsStyled>
                            <Button
                                className="mt-4 w-full self-start sm:hidden"
                                variant="positive"
                                text="Apply now"
                                onClick={() => {
                                    document
                                        .querySelector('#apply')
                                        .scrollIntoView({ behavior: 'smooth' });
                                }}
                            />
                        </HeaderStyled>
                        <MarkdownStyled>
                            <MDXRemote {...source} />
                        </MarkdownStyled>
                        <Link href="/jobs">
                            <a
                                className={classNames(
                                    twIconButton({ variant: 'secondary', shape: 'square' }),
                                    'absolute top-4 right-4 sm:top-6 sm:right-6'
                                )}
                                href="/jobs"
                            >
                                <CloseIcon />
                            </a>
                        </Link>
                        <ApplicationFormStyled
                            id="apply"
                            encType="multipart/form-data"
                            onSubmit={(e) => {
                                e.preventDefault();
                                dispatch(
                                    track({
                                        event: 'Submit Job Application',
                                        jobId: listing.playbackId,
                                        jobTitle: listing.title,
                                    })
                                );
                                handleSubmit(e);
                                submitLeverApplication(e);
                            }}
                        >
                            <ApplicationHeadingStyled>Apply now</ApplicationHeadingStyled>
                            <input hidden name="position" defaultValue={listing.title} />
                            <input hidden name="positionId" defaultValue={listing.playbackId} />
                            <input hidden name="leverId" defaultValue={listing.leverId} />
                            <ApplicationInputsStyled>
                                <TextInput
                                    className="mb-5 w-full sm:mb-0 sm:w-auto"
                                    type="text"
                                    label="Name"
                                    placeholder="LeBron James"
                                    name="name"
                                    disabled={formDisabled}
                                    required
                                />
                                <TextInput
                                    className="mb-5 w-full sm:mb-0 sm:w-auto"
                                    type="email"
                                    label="Email"
                                    placeholder="lebron@lakers.com"
                                    name="email"
                                    disabled={formDisabled}
                                    required
                                />
                            </ApplicationInputsStyled>
                            <Textarea
                                className="mb-5 sm:mb-8"
                                label="Why are you a good fit for this role?"
                                placeholder="Tell us a bit about your relevant experience and why you’d like to work at Playback."
                                name="summary"
                                disabled={formDisabled}
                                required
                            />
                            <label
                                className={classNames(
                                    twButton({ variant: 'secondary', disabled: formDisabled }),
                                    'mb-3 cursor-pointer self-start',
                                    {
                                        'shadow-negative-100': resumeFileError,
                                    }
                                )}
                            >
                                <UploadIcon />
                                {resumeFileName ? 'Replace resume' : 'Upload resume'}
                                <input
                                    hidden
                                    name="resume"
                                    disabled={formDisabled}
                                    type="file"
                                    accept=".pdf"
                                    required
                                    onChange={(e) => {
                                        const file = e.target.files.item(0);
                                        const sizeMB = file ? file.size / 1024 / 1024 : 0;

                                        if (sizeMB > 10) {
                                            e.target.value = '';
                                            setResumeFileError(true);
                                            setResumeFileName('');
                                        } else if (file) {
                                            setResumeFileError(false);
                                            setResumeFileName(file.name);
                                        }
                                    }}
                                />
                            </label>
                            <ApplicationResumeTextStyled
                                error={resumeFileError}
                                success={!!resumeFileName}
                            >
                                {resumeFileName && <CheckIcon />}
                                {resumeFileError && <AlertIcon />}
                                <span>{resumeFileName || 'Please upload a PDF under 10MB.'}</span>
                            </ApplicationResumeTextStyled>
                            <ApplicationInputsStyled>
                                <TextInput
                                    className="mb-5 w-full sm:mb-0 sm:w-auto"
                                    type="text"
                                    autoComplete="off"
                                    spellCheck={false}
                                    label="LinkedIn (optional)"
                                    placeholder="LinkedIn URL"
                                    name="linkedin"
                                    disabled={formDisabled}
                                />
                                <TextInput
                                    className="mb-5 w-full sm:mb-0 sm:w-auto"
                                    type="text"
                                    autoComplete="off"
                                    spellCheck={false}
                                    label="GitHub or personal site (optional)"
                                    placeholder="GitHub or other URL"
                                    name="website"
                                    disabled={formDisabled}
                                />
                            </ApplicationInputsStyled>
                            {state.succeeded ? (
                                <ApplicationSuccessStyled>
                                    <ApplicationSuccessIconStyled>
                                        <CheckIconStyled />
                                    </ApplicationSuccessIconStyled>
                                    <ApplicationSuccessTextStyled>
                                        You’re all set! We’ll review your application and get back
                                        to you ASAP.
                                    </ApplicationSuccessTextStyled>
                                </ApplicationSuccessStyled>
                            ) : (
                                <Button
                                    className="self-start sm:w-[120px]"
                                    type="submit"
                                    variant="positive"
                                    disabled={state.submitting}
                                    onClick={() => {
                                        if (!resumeFileName) {
                                            setResumeFileError(true);
                                        }
                                    }}
                                    text={state.submitting ? 'Submitting...' : 'Apply'}
                                />
                            )}
                        </ApplicationFormStyled>
                    </ListingStyled>
                </InnerStyled>
                <FooterStyled />
            </ContentsStyled>
        </JobListingStyled>
    );
};

const FooterStyled = styled(Footer)`
    padding: ${rem(20)};

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        padding: ${rem(40)};
    }
`;

const CheckIconStyled = styled(CheckIcon)`
    color: ${Base.Positive100};
    height: ${rem(24)};
    width: ${rem(24)};
`;

const ApplicationSuccessIconStyled = styled.div`
    height: ${rem(32)};
    width: ${rem(32)};
    margin-bottom: ${rem(16)};
    border-radius: 50%;
    border: ${rem(2)} solid ${Base.Positive100};
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: ${rem(20)};
`;

const ApplicationSuccessTextStyled = styled.p`
    ${globalBodyPrimary}
    color: ${Base.Positive100};
    text-align: center;
`;

const ApplicationSuccessStyled = styled.div`
    margin: ${rem(20)} auto 0;
    max-width: ${rem(360)};
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ApplicationResumeTextStyled = styled.p<{ error: boolean; success: boolean }>`
    ${globalBodySecondary}
    color: ${Base.Primary200};
    ${ellipsis()}
    margin-bottom: ${rem(20)};
    display: flex;
    align-items: center;

    svg {
        height: ${rem(16)};
        width: ${rem(16)};
        margin-right: ${rem(5)};
        flex-shrink: 0;
    }

    span {
        ${ellipsis()}
    }

    ${({ success }) =>
        success &&
        css`
            ${globalCtaSmall}
            color: ${Base.Positive100};
        `}

    ${({ error }) =>
        error &&
        css`
            ${globalCtaSmall}
            color: ${Base.Negative100};
        `}

    @media screen and (min-width: ${rem(Breakpoint.Medium)}) {
        margin-bottom: ${rem(32)};
    }
`;

const ApplicationInputsStyled = styled.div`
    display: flex;
    align-items: center;
    align-self: stretch;
    flex-wrap: wrap;
    margin-bottom: 0;

    @media screen and (min-width: ${rem(Breakpoint.Medium)}) {
        margin-bottom: ${rem(32)};
        flex-wrap: nowrap;
        gap: ${rem(16)};
    }
`;

const ApplicationHeadingStyled = styled.h2`
    ${heading3}
    color: ${Base.Primary100};
    margin-bottom: ${rem(24)};
    text-align: left;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        text-align: left;
        margin-bottom: ${rem(32)};
    }
`;

const ApplicationFormStyled = styled.form`
    max-width: ${rem(640)};
    width: 100%;
    margin: ${rem(40)} auto 0;
    padding: ${rem(20)} ${rem(20)} ${rem(28)};
    background-color: ${Base.Secondary400};
    border-radius: ${rem(12)};
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        padding: ${rem(40)} ${rem(40)} ${rem(48)};
        margin: ${rem(60)} auto 0;
    }
`;

const DateStyled = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: ${rem(16)};
`;

const ParagraphStyled = styled.p`
    ${globalBodyPrimary}
    color: ${Base.Primary200};
    text-align: left;
`;

const PrimaryHeadingStyled = styled.h1`
    ${fontMedium}
    color: ${Base.Primary100};
    font-size: ${rem(32)};
    line-height: 125%;
    margin-bottom: ${rem(16)};
    text-align: left;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        font-size: ${rem(40)};
        text-align: left;
        margin-bottom: ${rem(28)};
    }

    @media screen and (min-width: ${rem(Breakpoint.Medium)}) {
        font-size: ${rem(54)};
    }
`;

const MarkdownStyled = styled.div`
    color: ${Base.Primary200};
    max-width: ${rem(640)};
    margin: 0 auto;

    h1,
    h2,
    h3,
    h4,
    h5,
    strong {
        color: ${Base.Primary100};
    }

    strong {
        ${fontNormal}
    }

    p {
        ${globalBodyPrimary}
        color: ${Base.Primary200};

        &:not(:first-of-type) {
            margin-top: ${rem(16)};
        }
    }

    h1 + p {
        max-width: ${rem(480)};
        margin-bottom: ${rem(16)};

        @media screen and (min-width: ${rem(Breakpoint.Small)}) {
            text-align: center;
            margin: 0 auto ${rem(32)};
        }
    }

    h1 {
        margin-bottom: ${rem(16)};

        @media screen and (min-width: ${rem(Breakpoint.Small)}) {
            text-align: center;
            margin-bottom: ${rem(24)};
        }
    }

    h3 {
        margin: ${rem(20)} 0 ${rem(16)};
        ${globalTitle}
        color: ${Base.Primary100};

        &:first-child {
            margin-top: 0;
        }

        @media screen and (min-width: ${rem(Breakpoint.Small)}) {
            margin: ${rem(24)} 0 ${rem(16)};
            ${fontMedium}
            font-size: ${rem(24)};
            line-height: 125%;
            color: ${Base.Primary100};

            &:first-child {
                margin-top: 0;
            }
        }
    }

    li {
        list-style-type: disc;
        margin-left: ${rem(16)};
        margin-top: ${rem(8)};

        @media screen and (min-width: ${rem(Breakpoint.Small)}) {
            margin-left: ${rem(24)};
        }
    }
`;

const TitleSectionStyled = styled.div`
    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        flex: 3;
    }

    @media screen and (min-width: ${rem(Breakpoint.Medium)}) {
        flex: 1;
    }
`;

const DetailsStyled = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(140)}, 1fr));
    margin-top: ${rem(24)};

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        flex: 2;
        margin-top: 0;
        padding: 0 ${rem(40)} 0 ${rem(80)};
    }

    @media screen and (min-width: ${rem(Breakpoint.Medium)}) {
        flex: 1;
    }
`;

const HeaderStyled = styled.div`
    display: flex;
    flex-direction: column;
    padding-bottom: ${rem(32)};
    margin-bottom: ${rem(32)};

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        flex-direction: row;
        align-items: flex-start;
        flex-wrap: nowrap;
        padding-bottom: ${rem(40)};
        margin-bottom: ${rem(40)};
    }
`;

const ListingStyled = styled.div`
    padding: ${rem(24)} ${rem(24)} ${rem(40)};
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin: 0 auto;
    max-width: ${rem(1120)};
    width: 100%;
    background-color: ${Base.Tertiary100};
    box-sizing: border-box;
    position: relative;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        border-radius: ${rem(12)};
        padding: ${rem(40)} ${rem(40)} ${rem(80)};
    }
`;

const InnerStyled = styled.div`
    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        padding: ${rem(40)};
    }

    @media screen and (min-width: ${rem(Breakpoint.Medium)}) {
        padding: ${rem(40)} ${rem(80)};
    }
`;

const ContentsStyled = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 1;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        overflow-y: auto;
    }
`;

const NavLoggedInStyled = styled(NavLoggedIn)`
    display: none;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        display: flex;
    }
`;

const JobListingStyled = styled.div`
    background-color: black;

    @media screen and (min-width: ${rem(Breakpoint.Small)}) {
        height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: stretch;
    }
`;

JobListing.Layout = StandardLayout;

export default JobListing;
